import type { Event, EventHint, SeverityLevel } from '@sentry/browser';
import {
    addBreadcrumb,
    BrowserTracing,
    captureException,
    captureMessage,
    init,
    Replay,
} from '@sentry/browser';

import { DriverI, LogFn } from '../types';

/**
 * SentryDriverConfig
 *
 * Config shape for the SentryDriver type
 */
export type SentryDriverConfig = {
    type: 'sentry';
    dsn: string;
    release: string;
    environment: string;
    tenant: string;
};

const stringifyLogArg = (value: unknown): string => {
    if (typeof value === 'string') return value;
    if (value instanceof Error) return value.message;
    if (value === undefined) return 'undefined';

    try {
        const json = JSON.stringify(value);
        return json ?? String(value);
    } catch {
        return String(value);
    }
};

const addLoggerBreadcrumb = (level: SeverityLevel, args: unknown[]) => {
    addBreadcrumb({
        category: 'logger',
        level,
        message: stringifyLogArg(args[0]),
        data: { args },
    });
};

const browserNameFromEvent = (event: Event): string => {
    const fromContext = event.contexts?.browser?.name;
    if (typeof fromContext === 'string') return fromContext;

    const fromTag = event.tags?.['browser.name'] ?? event.tags?.browser;
    if (typeof fromTag === 'string') return fromTag;

    return '';
};

const userAgentFromEvent = (event: Event): string => {
    const headers = event.request?.headers;
    if (!headers) return '';

    return headers['User-Agent'] ?? headers['user-agent'] ?? '';
};

/**
 * shouldDropSentryEvent
 *
 * Drops HeadlessChrome traffic and unhandled rejections of DOM Event objects
 * (Sentry groups those as "<unknown>" / "Event captured as promise rejection").
 */
export const shouldDropSentryEvent = (
    event: Event,
    hint?: EventHint,
): boolean => {
    const browserName = browserNameFromEvent(event);
    const userAgent = userAgentFromEvent(event);

    if (
        browserName.includes('HeadlessChrome') ||
        /HeadlessChrome/i.test(userAgent)
    ) {
        return true;
    }

    const original = hint?.originalException;
    if (typeof Event !== 'undefined' && original instanceof Event) {
        return true;
    }

    const exceptionMessage = event.exception?.values?.[0]?.value ?? '';
    if (exceptionMessage.includes('captured as promise rejection')) {
        return true;
    }

    return false;
};

export const SentryDriver = (config: SentryDriverConfig): DriverI => {
    /**
     * debug
     *
     * Records a debug breadcrumb in Sentry
     */
    const debug: LogFn = (...args) => {
        addLoggerBreadcrumb('debug', args);
    };

    /**
     * info
     *
     * Records an info breadcrumb in Sentry
     */
    const info: LogFn = (...args) => {
        addLoggerBreadcrumb('info', args);
    };

    /**
     * log
     *
     * Records a log breadcrumb in Sentry
     */
    const log: LogFn = (...args) => {
        addLoggerBreadcrumb('log', args);
    };

    /**
     * warn
     *
     * Records a warning breadcrumb in Sentry
     */
    const warn: LogFn = (...args) => {
        addLoggerBreadcrumb('warning', args);
    };

    /**
     * error
     *
     * Sends an error level event to Sentry
     */
    const error: LogFn = (...args) => {
        if (args[0] instanceof Error) {
            captureException(args[0], {
                extra: {
                    data: args,
                },
            });
            return;
        }

        captureMessage(stringifyLogArg(args[0]), {
            level: 'error',
            extra: {
                data: args,
            },
        });
    };

    init({
        dsn: config.dsn,
        release: config.release,
        environment: config.environment,
        integrations: [new BrowserTracing(), new Replay()],

        // Capture 10% of transactions for performance monitoring.
        tracesSampleRate: 0.1,

        // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: [
            'localhost',
            /^https:\/\/.+\.suggestedtv\.com\/api/,
        ],

        // Capture Replay for 10% of all sessions,
        // plus for 100% of sessions with an error
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        initialScope: {
            tags: {
                tenant: config.tenant,
            },
        },
        beforeSend: (event, hint) =>
            shouldDropSentryEvent(event, hint) ? null : event,
    });

    return { debug, info, log, warn, error };
};
